import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, TrendingUp, Euro, Download, Briefcase, Phone, Mail, StickyNote, CheckSquare, Square, Sparkles, Clock as ClockIcon, Calendar, Building2, FileText, Receipt, ExternalLink, LayoutGrid, CalendarDays } from 'lucide-react';
import api from '../api/client';
import FormDialog from '../components/FormDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import DetailSheet from '../components/DetailSheet';
import RowActions from '../components/RowActions';
import EmptyState from '../components/EmptyState';
import { SkeletonCard } from '../components/Skeleton';
import { exportToCsv } from '../utils/export';
import { sortNewestFirst } from '../utils/sort';
import { toast } from '../hooks/use-toast';
import CRMCalendar from './_crm/CRMCalendar';

const crmStages = ['Nouveau', 'Qualification', 'Proposition', 'Négociation', 'Gagné'];
const STAGE_COLORS = { 'Nouveau': '#8E8FA5', 'Qualification': '#3B82F6', 'Proposition': '#F59E0B', 'Négociation': '#4F46E5', 'Gagné': '#14B8A6' };

const ACTIVITY_META = {
  note:    { icon: StickyNote, color: '#4F46E5', label: 'Note', bg: '#EEF2FF' },
  call:    { icon: Phone,      color: '#14B8A6', label: 'Appel', bg: '#E6FBF7' },
  email:   { icon: Mail,       color: '#3B82F6', label: 'Email', bg: '#E0F2FE' },
  meeting: { icon: Calendar,   color: '#F59E0B', label: 'RDV', bg: '#FEF3E7' },
  task:    { icon: CheckSquare,color: '#EC4899', label: 'Tâche', bg: '#FDF4FF' },
};

const FIELDS = [
  { name: 'company', label: 'Entreprise', required: true, full: true },
  { name: 'contact', label: 'Contact', required: true },
  { name: 'email', label: 'Email', type: 'email', required: true },
  { name: 'value', label: 'Valeur (€)', type: 'number', required: true },
  { name: 'probability', label: 'Probabilité (%)', type: 'number', required: true },
  { name: 'stage', label: 'Étape', type: 'select', options: crmStages, required: true, full: true },
];

export default function CRM() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pipeline'); // pipeline | calendar
  const [items, setItems] = useState([]);
  const [create, setCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [detail, setDetail] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pipeline, setPipeline] = useState(null);
  const [activities, setActivities] = useState([]);
  const [newAct, setNewAct] = useState({ type: 'note', title: '', content: '', due_date: '' });
  const [converting, setConverting] = useState(false);
  const [view360, setView360] = useState(null);
  const [emailActivity, setEmailActivity] = useState([]);
  const [emailActivityLoading, setEmailActivityLoading] = useState(false);

  useEffect(() => {
    api.get('/prospects').then(({ data }) => setItems(sortNewestFirst(data))).catch(() => {}).finally(() => setLoading(false));
    api.get('/crm/pipeline').then(({ data }) => setPipeline(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (detail) {
      api.get(`/prospects/${detail.id}/activities`).then(({ data }) => setActivities(data)).catch(() => setActivities([]));
      api.get(`/prospects/${detail.id}/360`).then(({ data }) => setView360(data)).catch(() => setView360(null));
      if (detail.email) {
        setEmailActivityLoading(true);
        api.get(`/mailbox/by-contact/${encodeURIComponent(detail.email)}`)
          .then(({ data }) => setEmailActivity(data?.threads || []))
          .catch(() => setEmailActivity([]))
          .finally(() => setEmailActivityLoading(false));
      } else {
        setEmailActivity([]);
      }
    } else { setActivities([]); setView360(null); setNewAct({ type: 'note', title: '', content: '', due_date: '' }); }
  }, [detail]);

  const refreshPipeline = () => api.get('/crm/pipeline').then(({ data }) => setPipeline(data)).catch(() => {});

  const handleCreate = async (values) => {
    const payload = { ...values, value: Number(values.value) || 0, probability: Number(values.probability) || 20 };
    const { data } = await api.post('/prospects', payload);
    setItems((prev) => [...prev, data]);
    toast({ title: 'Opportunité ajoutée', description: data.company });
  };

  const handleUpdate = async (values) => {
    const { data } = await api.put(`/prospects/${editing.id}`, { ...values, value: Number(values.value), probability: Number(values.probability) });
    setItems((prev) => prev.map((p) => p.id === data.id ? data : p));
    toast({ title: 'Opportunité modifiée', description: data.company });
  };

  const handleDelete = async () => {
    await api.delete(`/prospects/${deleting.id}`);
    setItems((prev) => prev.filter((p) => p.id !== deleting.id));
    toast({ title: 'Opportunité supprimée' });
  };

  const moveStage = async (id, stage) => {
    const prospect = items.find((p) => p.id === id);
    if (!prospect || prospect.stage === stage) return;
    setItems((prev) => prev.map((p) => p.id === id ? { ...p, stage } : p));
    try {
      await api.put(`/prospects/${id}`, { ...prospect, stage });
      toast({ title: 'Étape mise à jour', description: `${prospect.company} → ${stage}` });
      refreshPipeline();
    } catch {
      // rollback
      setItems((prev) => prev.map((p) => p.id === id ? prospect : p));
    }
  };

  const addActivity = async () => {
    if (!detail) return;
    const title = newAct.title.trim() || ACTIVITY_META[newAct.type].label;
    try {
      const { data } = await api.post(`/prospects/${detail.id}/activities`, { ...newAct, title });
      setActivities((prev) => [data, ...prev]);
      setNewAct({ type: 'note', title: '', content: '', due_date: '' });
      toast({ title: 'Activité ajoutée', description: `${ACTIVITY_META[data.type].label} · ${data.title}` });
    } catch {
      toast({ title: 'Erreur' });
    }
  };

  const toggleTask = async (a) => {
    try {
      const { data } = await api.put(`/activities/${a.id}`, { completed: !a.completed });
      setActivities((prev) => prev.map((x) => x.id === a.id ? data : x));
    } catch {}
  };

  const removeActivity = async (a) => {
    try {
      await api.delete(`/activities/${a.id}`);
      setActivities((prev) => prev.filter((x) => x.id !== a.id));
    } catch {}
  };

  const convertProspect = async (createQuote) => {
    if (!detail) return;
    setConverting(true);
    try {
      const { data } = await api.post(`/prospects/${detail.id}/convert`, { create_company: true, create_quote: createQuote });
      const bits = [];
      if (data.company) bits.push(`Entreprise ${data.company.name}`);
      if (data.quote) bits.push(`Devis ${data.quote.id}`);
      toast({ title: 'Opportunité convertie 🎉', description: bits.join(' · ') || 'Statut → Gagné' });
      setItems((prev) => prev.map((p) => p.id === detail.id ? { ...p, stage: 'Gagné' } : p));
      setDetail((d) => d ? { ...d, stage: 'Gagné' } : d);
      refreshPipeline();
      // Re-fetch 360° so the newly linked company/quote appears instantly
      try { const r = await api.get(`/prospects/${detail.id}/360`); setView360(r.data); } catch { /* noop */ }
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Conversion impossible' });
    } finally {
      setConverting(false);
    }
  };

  const onDragStart = (e, id) => { e.dataTransfer.setData('text/plain', id); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e, stage) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(stage); };
  const onDrop = (e, stage) => { e.preventDefault(); const id = e.dataTransfer.getData('text/plain'); moveStage(id, stage); setDragOver(null); };

  const totalPipeline = items.reduce((s, p) => s + p.value, 0);
  const weighted = items.reduce((s, p) => s + (p.value * p.probability) / 100, 0);

  return (
    <div className="space-y-5">
      {/* Vague 113 — tab switcher pipeline vs calendrier */}
      <div className="flex items-center gap-1 bg-white border border-[#EFF0F6] rounded-xl p-1 w-fit" data-testid="crm-tab-switch">
        <button onClick={() => setTab('pipeline')} data-testid="crm-tab-pipeline"
                className={`h-9 px-3.5 rounded-lg text-[12.5px] font-semibold flex items-center gap-1.5 ${tab==='pipeline' ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85] hover:bg-[#FAFAFE]'}`}>
          <LayoutGrid className="w-3.5 h-3.5" /> Pipeline
        </button>
        <button onClick={() => setTab('calendar')} data-testid="crm-tab-calendar"
                className={`h-9 px-3.5 rounded-lg text-[12.5px] font-semibold flex items-center gap-1.5 ${tab==='calendar' ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85] hover:bg-[#FAFAFE]'}`}>
          <CalendarDays className="w-3.5 h-3.5" /> Calendrier
        </button>
      </div>

      {tab === 'calendar' ? <CRMCalendar /> : <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="soft-card p-4"><div className="text-[12px] text-[#8E8FA5]">Pipeline total</div><div className="text-[22px] font-bold text-[#4F46E5] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{totalPipeline.toLocaleString('fr-FR')} €</div></div>
        <div className="soft-card p-4"><div className="text-[12px] text-[#8E8FA5]">Pondéré</div><div className="text-[22px] font-bold text-[#14B8A6] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{Math.round(weighted).toLocaleString('fr-FR')} €</div></div>
        <div className="soft-card p-4"><div className="text-[12px] text-[#8E8FA5]">Tâches en retard</div><div className="text-[22px] font-bold text-[#B91C1C] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{pipeline?.overdue_tasks ?? 0}</div></div>
        <div className="soft-card p-4"><div className="text-[12px] text-[#8E8FA5]">Gagnés</div><div className="text-[22px] font-bold text-[#3B82F6] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{items.filter((p) => p.stage === 'Gagné').length}</div></div>
      </div>

      {/* Funnel visual */}
      {pipeline && (
        <div className="soft-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[14px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Tunnel de conversion</h3>
            <span className="text-[11.5px] text-[#8E8FA5]">{pipeline.total_prospects} opportunité(s) · {pipeline.upcoming_tasks} tâche(s) à venir</span>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {pipeline.funnel.map((f) => {
              const maxCount = Math.max(...pipeline.funnel.map((x) => x.count), 1);
              return (
                <div key={f.stage} data-testid={`funnel-${f.stage}`} className="rounded-xl p-3" style={{ background: `${STAGE_COLORS[f.stage]}14` }}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: STAGE_COLORS[f.stage] }} />
                    <span className="text-[11.5px] font-semibold text-[#1A1B2E]">{f.stage}</span>
                  </div>
                  <div className="text-[20px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{f.count}</div>
                  <div className="h-1 rounded-full bg-white/70 overflow-hidden mt-1.5 mb-1.5">
                    <div className="h-full rounded-full" style={{ width: `${(f.count / maxCount) * 100}%`, background: STAGE_COLORS[f.stage] }} />
                  </div>
                  <div className="text-[11px] text-[#4A4B63]">{f.total.toLocaleString('fr-FR')} €</div>
                  <div className="text-[10.5px] text-[#8E8FA5]">pondéré {f.weighted.toLocaleString('fr-FR')} €</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="text-[12.5px] text-[#8E8FA5]">💡 Glissez-déposez les cartes entre les colonnes pour changer d'étape.</div>
        <button onClick={() => exportToCsv('prospects', items, ['id','company','contact','email','value','probability','stage'])} className="h-10 px-3.5 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-medium text-[#4A4B63] flex items-center gap-2 hover:border-[#C7D2FE] ml-auto">
          <Download className="w-4 h-4" /> Exporter
        </button>
        <button onClick={() => setCreate(true)} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm hover:opacity-95">
          <Plus className="w-4 h-4" /> Nouvelle opportunité
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {loading && Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="kanban-col"><SkeletonCard /></div>
        ))}
        {!loading && items.length === 0 && (
          <div className="col-span-full">
            <EmptyState icon={Briefcase} title="Aucune opportunité" description="Créez votre première opportunité commerciale pour démarrer votre pipeline." action={<button onClick={() => setCreate(true)} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold">Nouvelle opportunité</button>} />
          </div>
        )}
        {!loading && items.length > 0 && crmStages.map((stage) => {
          const stageItems = items.filter((p) => p.stage === stage);
          const stageValue = stageItems.reduce((s, p) => s + p.value, 0);
          return (
            <div
              key={stage}
              onDragOver={(e) => onDragOver(e, stage)}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => onDrop(e, stage)}
              className={`kanban-col transition-colors ${dragOver === stage ? 'bg-[#EDE8FF]' : ''}`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: STAGE_COLORS[stage] }} />
                  <span className="text-[13px] font-semibold text-[#1A1B2E]">{stage}</span>
                  <span className="text-[11px] text-[#8E8FA5]">({stageItems.length})</span>
                </div>
              </div>
              <div className="text-[11.5px] text-[#8E8FA5] mb-2 px-1">{stageValue.toLocaleString('fr-FR')} €</div>
              <div className="space-y-2">
                {stageItems.map((p) => {
                  const days = p.last_activity_at
                    ? Math.floor((Date.now() - new Date(p.last_activity_at).getTime()) / 86400000)
                    : null;
                  const freshness = days === null
                    ? { color: '#C7D2FE', label: 'Aucune activité', icon: '—' }
                    : days <= 7
                      ? { color: '#14B8A6', label: `Contacté il y a ${days}j` , icon: '●' }
                      : days <= 14
                        ? { color: '#F59E0B', label: `Dernier contact il y a ${days}j — relance bientôt`, icon: '●' }
                        : { color: '#EF4444', label: `Aucun contact depuis ${days}j — à relancer !`, icon: '●' };
                  return (
                  <div
                    key={p.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, p.id)}
                    onClick={() => setDetail(p)}
                    className="relative bg-white rounded-xl p-3 border border-[#EFF0F6] cursor-grab active:cursor-grabbing hover:shadow-sm hover:border-[#C7D2FE] transition-all"
                  >
                    <span
                      data-testid={`crm-freshness-${p.id}`}
                      title={freshness.label}
                      className="absolute top-2.5 left-2.5 w-2 h-2 rounded-full"
                      style={{ backgroundColor: freshness.color, boxShadow: `0 0 0 3px ${freshness.color}22` }}
                    />
                    <div className="flex items-start justify-between gap-2 pl-4">
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-[#1A1B2E] leading-snug truncate">{p.company}</div>
                        <div className="text-[11.5px] text-[#6B6C85] mt-0.5 truncate">{p.contact}</div>
                      </div>
                      <RowActions onView={() => setDetail(p)} onEdit={() => setEditing(p)} onDelete={() => setDeleting(p)} />
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="inline-flex items-center gap-1 text-[12px] font-bold text-[#1A1B2E]">
                        <Euro className="w-3 h-3 text-[#8E8FA5]" />
                        {(p.value / 1000).toFixed(1)}k
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5]">
                        <TrendingUp className="w-2.5 h-2.5" /> {p.probability}%
                      </span>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <FormDialog open={create} onOpenChange={setCreate} title="Nouvelle opportunité" fields={FIELDS} onSubmit={handleCreate} />
      <FormDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title="Modifier l'opportunité" fields={FIELDS} initialValues={editing} onSubmit={handleUpdate} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title="Supprimer cette opportunité ?" description={deleting?.company} onConfirm={handleDelete} />
      <DetailSheet
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
        title={detail?.company}
        subtitle={detail?.contact}
        badge={detail && <span className="inline-flex text-[11px] font-semibold px-2 py-0.5 rounded" style={{ background: `${STAGE_COLORS[detail.stage]}22`, color: STAGE_COLORS[detail.stage] }}>{detail.stage}</span>}
        rows={detail ? [
          { label: 'Email', value: detail.email },
          { label: 'Valeur', value: `${detail.value?.toLocaleString('fr-FR')} €` },
          { label: 'Probabilité', value: `${detail.probability}%` },
          { label: 'Valeur pondérée', value: `${Math.round((detail.value * detail.probability) / 100).toLocaleString('fr-FR')} €` },
        ] : []}
      >
        {detail && (
          <div className="mt-4 pt-4 border-t border-[#EFF0F6] space-y-4">
            {/* Convert bar */}
            {detail.stage !== 'Gagné' && (
              <div className="flex gap-2">
                <button
                  data-testid="crm-convert-simple"
                  disabled={converting}
                  onClick={() => convertProspect(false)}
                  className="flex-1 h-10 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60"
                >
                  <Sparkles className="w-4 h-4" /> Convertir en entreprise
                </button>
                <button
                  data-testid="crm-convert-with-quote"
                  disabled={converting}
                  onClick={() => convertProspect(true)}
                  className="flex-1 h-10 rounded-xl bg-white border border-[#C7D2FE] text-[#4F46E5] text-[13px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#EEF2FF] disabled:opacity-60"
                >
                  + Créer un devis
                </button>
              </div>
            )}

            {/* Parcours commercial (360°) */}
            {view360 && (
              <div className="rounded-xl border border-[#EFF0F6] bg-white overflow-hidden" data-testid="crm-360">
                <div className="px-3 py-2.5 bg-[#FAFAFE] border-b border-[#EFF0F6] flex items-center justify-between">
                  <div className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5]">Parcours commercial</div>
                  <div className="text-[10.5px] text-[#8E8FA5]">
                    {view360.kpis.quotes_count} devis · {view360.kpis.invoices_count} facture(s)
                  </div>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 gap-2 p-3 bg-[#FAFAFE]">
                  <div className="rounded-lg bg-white p-2 border border-[#EFF0F6]">
                    <div className="text-[10px] text-[#8E8FA5] uppercase tracking-wider">Devis signés</div>
                    <div className="text-[14px] font-bold text-[#0F766E]">{view360.kpis.quotes_accepted.toLocaleString('fr-FR')} €</div>
                    <div className="text-[10px] text-[#8E8FA5]">sur {view360.kpis.quotes_total.toLocaleString('fr-FR')} € proposés</div>
                  </div>
                  <div className="rounded-lg bg-white p-2 border border-[#EFF0F6]">
                    <div className="text-[10px] text-[#8E8FA5] uppercase tracking-wider">CA facturé</div>
                    <div className="text-[14px] font-bold text-[#4F46E5]">{view360.kpis.invoices_paid.toLocaleString('fr-FR')} €</div>
                    <div className="text-[10px] text-[#8E8FA5]">
                      {view360.kpis.invoices_pending > 0 && <span>· {view360.kpis.invoices_pending.toLocaleString('fr-FR')} € en attente</span>}
                      {view360.kpis.invoices_overdue > 0 && <span className="text-[#B91C1C]"> · {view360.kpis.invoices_overdue.toLocaleString('fr-FR')} € retard</span>}
                    </div>
                  </div>
                </div>

                {/* Entreprise liée */}
                {view360.company ? (
                  <button
                    data-testid="crm-360-company"
                    onClick={() => navigate('/app/entreprises')}
                    className="w-full px-3 py-2.5 flex items-center gap-2 border-t border-[#EFF0F6] hover:bg-[#EEF2FF] transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#EEF2FF] flex items-center justify-center shrink-0">
                      <Building2 className="w-4 h-4 text-[#4F46E5]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{view360.company.name}</div>
                      <div className="text-[11px] text-[#8E8FA5] truncate">
                        {view360.company.siret ? `SIRET ${view360.company.siret}` : view360.company.contact_email || '—'}
                      </div>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-[#8E8FA5] shrink-0" />
                  </button>
                ) : (
                  <div className="px-3 py-2.5 text-[11.5px] text-[#8E8FA5] italic border-t border-[#EFF0F6]">
                    Aucune entreprise rattachée — utilisez « Convertir » pour créer la fiche.
                  </div>
                )}

                {/* Devis liés */}
                {view360.quotes.length > 0 && (
                  <div className="border-t border-[#EFF0F6]">
                    <div className="px-3 py-2 text-[10.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] bg-white">Devis ({view360.quotes.length})</div>
                    <div className="divide-y divide-[#F3F4F8] max-h-[180px] overflow-y-auto">
                      {view360.quotes.slice(0, 8).map((q) => (
                        <button
                          key={q.id}
                          data-testid={`crm-360-quote-${q.id}`}
                          onClick={() => navigate('/app/devis')}
                          className="w-full px-3 py-2 flex items-center gap-2 hover:bg-[#FAFAFE] transition-colors text-left"
                        >
                          <FileText className="w-3.5 h-3.5 text-[#4F46E5] shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] font-semibold text-[#1A1B2E] font-mono truncate">{q.id}</div>
                            <div className="text-[10.5px] text-[#8E8FA5] truncate">{q.date} · {q.status}</div>
                          </div>
                          <span className="text-[12px] font-bold text-[#1A1B2E] shrink-0">{Number(q.amount || 0).toLocaleString('fr-FR')} €</span>
                        </button>
                      ))}
                      {view360.quotes.length > 8 && (
                        <div className="px-3 py-1.5 text-[10.5px] text-[#8E8FA5] italic text-center">+ {view360.quotes.length - 8} autre(s)</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Factures liées */}
                {view360.invoices.length > 0 && (
                  <div className="border-t border-[#EFF0F6]">
                    <div className="px-3 py-2 text-[10.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] bg-white">Factures ({view360.invoices.length})</div>
                    <div className="divide-y divide-[#F3F4F8] max-h-[180px] overflow-y-auto">
                      {view360.invoices.slice(0, 8).map((i) => (
                        <button
                          key={i.id}
                          data-testid={`crm-360-invoice-${i.id}`}
                          onClick={() => navigate('/app/factures')}
                          className="w-full px-3 py-2 flex items-center gap-2 hover:bg-[#FAFAFE] transition-colors text-left"
                        >
                          <Receipt className="w-3.5 h-3.5 text-[#14B8A6] shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] font-semibold text-[#1A1B2E] font-mono truncate">{i.id}</div>
                            <div className="text-[10.5px] text-[#8E8FA5] truncate">{i.date} · {i.status}</div>
                          </div>
                          <span className={`text-[12px] font-bold shrink-0 ${i.status === 'Payée' ? 'text-[#0F766E]' : i.status === 'En retard' ? 'text-[#B91C1C]' : 'text-[#1A1B2E]'}`}>
                            {Number(i.amount || 0).toLocaleString('fr-FR')} €
                          </span>
                        </button>
                      ))}
                      {view360.invoices.length > 8 && (
                        <div className="px-3 py-1.5 text-[10.5px] text-[#8E8FA5] italic text-center">+ {view360.invoices.length - 8} autre(s)</div>
                      )}
                    </div>
                  </div>
                )}

                {view360.quotes.length === 0 && view360.invoices.length === 0 && (
                  <div className="border-t border-[#EFF0F6] px-3 py-3 text-[11.5px] text-[#8E8FA5] italic text-center">
                    Aucun devis ni facture encore lié. Créez-en un via « + Créer un devis ».
                  </div>
                )}
              </div>
            )}

            {/* Email activity (Wave 1f) — emails échangés avec ce contact */}
            {detail?.email && (
              <div className="rounded-xl border border-[#EFF0F6] bg-white overflow-hidden" data-testid="crm-email-activity">
                <div className="px-3 py-2.5 bg-[#FAFAFE] border-b border-[#EFF0F6] flex items-center justify-between">
                  <div className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] flex items-center gap-1.5">
                    <Mail className="w-3 h-3" /> Activité email
                  </div>
                  <Link
                    to={`/app/email?q=${encodeURIComponent(detail.email)}`}
                    className="text-[10.5px] text-[#4F46E5] hover:underline"
                    data-testid="crm-email-activity-open"
                  >
                    Voir dans Boîte email →
                  </Link>
                </div>
                {emailActivityLoading ? (
                  <div className="px-3 py-4 text-[12px] text-[#8E8FA5] italic text-center">Chargement…</div>
                ) : emailActivity.length === 0 ? (
                  <div className="px-3 py-4 text-[12px] text-[#8E8FA5] italic text-center">
                    Aucun échange email avec {detail.email}.
                  </div>
                ) : (
                  <div className="divide-y divide-[#F3F4F8] max-h-[260px] overflow-y-auto">
                    {emailActivity.slice(0, 12).map((t) => (
                      <Link
                        key={t.thread_id}
                        to={`/app/email?thread=${t.thread_id}`}
                        data-testid={`crm-email-thread-${t.thread_id}`}
                        className="block px-3 py-2 hover:bg-[#FAFAFE]"
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className={`text-[12px] truncate ${t.unread ? 'font-bold text-[#1A1B2E]' : 'font-medium text-[#4A4B63]'}`}>
                            {t.from === '(vous)' ? `→ ${t.to}` : t.from}
                          </span>
                          <span className="text-[10px] text-[#8E8FA5] flex-shrink-0">
                            {t.date_ms ? new Date(t.date_ms).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : ''}
                          </span>
                        </div>
                        <div className={`text-[12px] truncate mt-0.5 ${t.unread ? 'font-semibold text-[#1A1B2E]' : 'text-[#6B6C85]'}`}>
                          {t.subject}
                        </div>
                        {t.snippet && (
                          <div className="text-[11px] text-[#8E8FA5] truncate mt-0.5">{t.snippet}</div>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Quick add activity */}
            <div className="rounded-xl border border-[#EFF0F6] p-3 bg-[#FAFAFE]">
              <div className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] mb-2">Nouvelle activité</div>
              <div className="flex gap-1.5 mb-2">
                {Object.entries(ACTIVITY_META).map(([k, m]) => {
                  const Ic = m.icon;
                  return (
                    <button
                      key={k}
                      data-testid={`activity-type-${k}`}
                      onClick={() => setNewAct((a) => ({ ...a, type: k }))}
                      className={`h-8 px-2.5 rounded-lg text-[11.5px] font-semibold flex items-center gap-1 transition-colors ${newAct.type === k ? 'text-white' : 'text-[#4A4B63] bg-white border border-[#EFF0F6]'}`}
                      style={newAct.type === k ? { background: m.color } : {}}
                    >
                      <Ic className="w-3.5 h-3.5" /> {m.label}
                    </button>
                  );
                })}
              </div>
              <input
                data-testid="activity-title-input"
                value={newAct.title}
                onChange={(e) => setNewAct((a) => ({ ...a, title: e.target.value }))}
                placeholder="Titre (ex: Appel de suivi, Rappeler mardi…)"
                className="w-full h-9 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px] outline-none focus:border-[#C7D2FE]"
              />
              <textarea
                value={newAct.content}
                onChange={(e) => setNewAct((a) => ({ ...a, content: e.target.value }))}
                placeholder="Contenu / notes (optionnel)"
                className="mt-1.5 w-full h-16 px-3 py-2 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px] outline-none focus:border-[#C7D2FE] resize-y"
              />
              {newAct.type === 'task' && (
                <input
                  type="date"
                  data-testid="activity-due-input"
                  value={newAct.due_date}
                  onChange={(e) => setNewAct((a) => ({ ...a, due_date: e.target.value }))}
                  className="mt-1.5 w-full h-9 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px] outline-none"
                />
              )}
              <button
                data-testid="activity-add-btn"
                onClick={addActivity}
                className="mt-2 h-9 px-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" /> Ajouter
              </button>
            </div>

            {/* Timeline */}
            <div>
              <div className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] mb-2">Historique ({activities.length})</div>
              {activities.length === 0 && <div className="text-[12.5px] italic text-[#8E8FA5]">Aucune activité pour le moment.</div>}
              <div className="space-y-2">
                {activities.map((a) => {
                  const m = ACTIVITY_META[a.type] || ACTIVITY_META.note;
                  const Ic = m.icon;
                  const isTaskOverdue = a.type === 'task' && !a.completed && a.due_date && a.due_date < new Date().toISOString().slice(0, 10);
                  return (
                    <div key={a.id} data-testid={`activity-${a.id}`} className="rounded-xl border border-[#EFF0F6] p-3 bg-white">
                      <div className="flex items-start gap-2">
                        <div className="w-7 h-7 rounded-lg shrink-0 flex items-center justify-center" style={{ background: m.bg, color: m.color }}>
                          <Ic className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {a.type === 'task' && (
                              <button onClick={() => toggleTask(a)} className="shrink-0">
                                {a.completed ? <CheckSquare className="w-4 h-4 text-[#14B8A6]" /> : <Square className="w-4 h-4 text-[#8E8FA5]" />}
                              </button>
                            )}
                            <span className={`text-[13px] font-semibold text-[#1A1B2E] ${a.completed ? 'line-through text-[#8E8FA5]' : ''}`}>{a.title}</span>
                            {isTaskOverdue && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FEF2F2] text-[#B91C1C]">En retard</span>}
                          </div>
                          {a.content && <div className="text-[12px] text-[#4A4B63] mt-1 whitespace-pre-wrap">{a.content}</div>}
                          <div className="text-[10.5px] text-[#8E8FA5] mt-1 flex items-center gap-2">
                            <ClockIcon className="w-3 h-3" />
                            {new Date(a.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            <span>·</span>
                            <span>{a.created_by_name || a.created_by}</span>
                            {a.due_date && <span>· échéance {new Date(a.due_date).toLocaleDateString('fr-FR')}</span>}
                          </div>
                        </div>
                        <button onClick={() => removeActivity(a)} className="w-7 h-7 rounded-lg hover:bg-[#FEF2F2] text-[#8E8FA5] hover:text-[#B91C1C] flex items-center justify-center text-[10px]">×</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DetailSheet>
      </>}
    </div>
  );
}
